// Treasury refund-requests module parser — DTO (wire shape) → view
// model. The seam every consumer must respect. Containers / components
// / composables never read a snake_case field directly.

import type {
  RefundAuditDto,
  RefundDetailDto,
  RefundInvestorDto,
  RefundOrderDto,
  RefundRowDto,
  RefundStatusCountsDto,
  RefundsListResponseDto,
} from './refundsContracts'
import {
  REFUND_METHOD,
  type RefundAudit,
  type RefundDetail,
  type RefundInvestor,
  type RefundMethod,
  type RefundOrder,
  type RefundRow,
  type RefundStatusCounts,
  type RefundsListResponse,
} from '../types/refundsTypes'

function pickMethod(value: RefundMethod | undefined): RefundMethod {
  return value ?? REFUND_METHOD.FIAT_WALLET
}

export function parseRefundRow(dto: RefundRowDto): RefundRow {
  return {
    id: String(dto.id ?? ''),
    orderId: String(dto.orderId ?? dto.order_id ?? ''),
    orderReference: String(dto.orderReference ?? dto.order_reference ?? ''),
    orderCancelledAt: dto.orderCancelledAt ?? dto.order_cancelled_at ?? null,
    investorId: String(dto.investorId ?? dto.investor_id ?? ''),
    investorName: String(dto.investorName ?? dto.investor_name ?? ''),
    investorEmail: String(dto.investorEmail ?? dto.investor_email ?? ''),
    amount: String(dto.amount ?? '0'),
    currencyCode: String(dto.currencyCode ?? dto.currency_code ?? ''),
    refundState: dto.refundState ?? dto.refund_state ?? 'pending',
    refundMethod: pickMethod(dto.refundMethod ?? dto.refund_method),
    notes: dto.notes ?? null,
    requestedAt: String(dto.requestedAt ?? dto.requested_at ?? ''),
    approvedAt: dto.approvedAt ?? dto.approved_at ?? null,
    approvedBy: dto.approvedBy ?? dto.approved_by ?? null,
    settledAt: dto.settledAt ?? dto.settled_at ?? null,
    settlementReference: dto.settlementReference ?? dto.settlement_reference ?? null,
  }
}

export function parseRefundsListResponse(dto: RefundsListResponseDto): RefundsListResponse {
  const limit = Number(dto.meta?.limit ?? 0)
  const total = Number(dto.meta?.total ?? 0)
  const page = Number(dto.meta?.page ?? 1)
  const totalPagesRaw = dto.meta?.totalPages ?? dto.meta?.total_pages
  const totalPages =
    totalPagesRaw !== undefined && totalPagesRaw !== null
      ? Number(totalPagesRaw)
      : limit > 0
        ? Math.ceil(total / limit)
        : 0
  return {
    data: (dto.data ?? []).map(parseRefundRow),
    meta: { total, page, limit, totalPages },
  }
}

export function parseRefundStatusCounts(dto: RefundStatusCountsDto): RefundStatusCounts {
  return {
    pending: Number(dto.pending ?? 0),
    settled: Number(dto.settled ?? 0),
    rejected: Number(dto.rejected ?? 0),
  }
}

function parseOrder(dto: RefundOrderDto): RefundOrder {
  return {
    id: String(dto.id ?? ''),
    orderReference: String(dto.orderReference ?? dto.order_reference ?? ''),
    orderStatus: String(dto.orderStatus ?? dto.order_status ?? ''),
    propertyName: dto.propertyName ?? dto.property_name ?? null,
    tokenQuantity:
      dto.tokenQuantity !== undefined && dto.tokenQuantity !== null
        ? Number(dto.tokenQuantity)
        : dto.token_quantity !== undefined && dto.token_quantity !== null
          ? Number(dto.token_quantity)
          : null,
    netAmount: String(dto.netAmount ?? dto.net_amount ?? '0'),
    currencyCode: String(dto.currencyCode ?? dto.currency_code ?? ''),
    paymentMethod: dto.paymentMethod ?? dto.payment_method ?? null,
    createdAt: String(dto.createdAt ?? dto.created_at ?? ''),
    paymentConfirmedAt: dto.paymentConfirmedAt ?? dto.payment_confirmed_at ?? null,
    rejectedAt: dto.rejectedAt ?? dto.rejected_at ?? null,
    cancelledAt: dto.cancelledAt ?? dto.cancelled_at ?? null,
  }
}

function parseInvestor(dto: RefundInvestorDto): RefundInvestor {
  return {
    id: String(dto.id ?? ''),
    firstName: dto.firstName ?? dto.first_name ?? null,
    lastName: dto.lastName ?? dto.last_name ?? null,
    email: String(dto.email ?? ''),
    kycStatus: dto.kycStatus ?? dto.kyc_status ?? null,
  }
}

function parseAudit(dto: RefundAuditDto | undefined): RefundAudit {
  return {
    approvedBy: dto?.approvedBy ?? dto?.approved_by ?? null,
    approvedAt: dto?.approvedAt ?? dto?.approved_at ?? null,
    settledAt: dto?.settledAt ?? dto?.settled_at ?? null,
    settlementReference: dto?.settlementReference ?? dto?.settlement_reference ?? null,
  }
}

export function parseRefundDetail(dto: RefundDetailDto): RefundDetail {
  return {
    id: String(dto.id ?? ''),
    orderId: String(dto.orderId ?? dto.order_id ?? ''),
    investorId: String(dto.investorId ?? dto.investor_id ?? ''),
    amount: String(dto.amount ?? '0'),
    currencyCode: String(dto.currencyCode ?? dto.currency_code ?? ''),
    refundState: dto.refundState ?? dto.refund_state ?? 'pending',
    refundMethod: pickMethod(dto.refundMethod ?? dto.refund_method),
    notes: dto.notes ?? null,
    requestedAt: String(dto.requestedAt ?? dto.requested_at ?? ''),
    approvedAt: dto.approvedAt ?? dto.approved_at ?? null,
    approvedBy: dto.approvedBy ?? dto.approved_by ?? null,
    settledAt: dto.settledAt ?? dto.settled_at ?? null,
    settlementReference: dto.settlementReference ?? dto.settlement_reference ?? null,
    order: parseOrder(dto.order),
    investor: parseInvestor(dto.investor),
    audit: parseAudit(dto.audit),
  }
}
